import PropTypes from 'prop-types'
import { getCostProfitMatricesByProjectId, getInstallationOptionById, getProjectChangeOrdersByProjectId, getProjectInstallerPOsByProjectId, getProjectProductionPOsByProjectId, getProjectShippingPOsByProjectId, upsertCostProfitMatrices } from 'api'
import { EMC_FACES, PROJECT_PRODUCTION_PO_CATEGORIES, PROJECT_INSTALLER_PO_CATEGORIES, getQuoteByProjectId, getQuotePriceBreakdown } from 'lib'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { MdExpandLess, MdExpandMore } from 'react-icons/md'
import { Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, Input, InputGroup, InputLeftAddon, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberInput, NumberInputField, Text, Textarea, useDisclosure } from '@chakra-ui/react'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'

const initialForm = {
  projectId: 0,
  emcCost: 0,
  emcText: 0,
  fabricationCost: 0,
  fabricationText: 0,
  monumentCost: 0,
  monumentText: 0,
  steelCost: 0,
  steelText: 0,
  shippingCost: 0,
  shippingText: 0,
  installationCost: 0,
  installationText: 0,
  taxCost: 0,
  taxText: 0,
  permittingCost: 0,
  permittingText: 0,
  varianceCost: 0,
  varianceText: 0,
  grossSale: 0,
  cogs: 0,
  overage: 0,
  discount: 0,
  profit: 0,
  margin: 0,
  notes: ''
}

export const ModalProjectCPM = ({ isOpen, onClose, projectId }) => {
  const { createToast } = useAppContext()
  const [cpm, setCpm] = useState(null)
  const [form, setForm] = useImmer(initialForm)
  const [quote, setQuote] = useState(null)
  const [installationOption, setInstallationOption] = useState(null)
  const [emcPOs, setEmcPOs] = useState([])
  const [fabricationPOs, setFabricationPOs] = useState([])
  const [monumentPOs, setMonumentPOs] = useState([])
  const [steelPOs, setSteelPOs] = useState([])
  const [shippingPOs, setShippingPOs] = useState([])
  const [installerPOs, setInstallerPOs] = useState([])
  const [changeOrders, setChangeOrders] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [isLocked, setIsLocked] = useState(true)
  const [isPOsOnly, setIsPOsOnly] = useState(false)
  const disclosureUpdateGrossSale = useDisclosure()

  const loadCPM = async () => {
    try {
      const data = await getCostProfitMatricesByProjectId(projectId)
      setCpm(data)
      setForm(draft => {
        draft.projectId = projectId
        draft.emcCost = data?.emcCost || 0
        draft.emcText = data?.emcText || 0
        draft.fabricationCost = data?.fabricationCost || 0
        draft.fabricationText = data?.fabricationText || 0
        draft.monumentCost = data?.monumentCost || 0
        draft.monumentText = data?.monumentText || 0
        draft.steelCost = data?.steelCost || 0
        draft.steelText = data?.steelText || 0
        draft.shippingCost = data?.shippingCost || 0
        draft.shippingText = data?.shippingText || 0
        draft.installationCost = data?.installationCost || 0
        draft.installationText = data?.installationText || 0
        draft.taxCost = data?.taxCost || 0
        draft.taxText = data?.taxText || 0
        draft.permittingCost = data?.permittingCost || 0
        draft.permittingText = data?.permittingText || 0
        draft.varianceCost = data?.varianceCost || 0
        draft.varianceText = data?.varianceText || 0
        draft.grossSale = data?.grossSale || 0
        draft.cogs = data?.cogs || 0
        draft.overage = data?.overage || 0
        draft.discount = data?.discount || 0
        draft.profit = data?.profit || 0
        draft.margin = data?.margin || 0
        draft.notes = data?.notes || ''
      })
    } catch (err) {
      // error log here
    }
  }

  const loadQuoteByProjectId = async () => {
    try {
      const data = await getQuoteByProjectId(projectId)
      setQuote(data)
      if (data?.installationOptionId) {
        try {
          const option = await getInstallationOptionById(data.installationOptionId)
          setInstallationOption(option)
        } catch (err) {
          setInstallationOption(null)
        }
      } else {
        setInstallationOption(null)
      }
    } catch (err) {
      // error log here
    }
  }

  const loadProjectProductionPOs = async () => {
    try {
      const data = await getProjectProductionPOsByProjectId(projectId)
      setEmcPOs(data.filter(v => v.name === PROJECT_PRODUCTION_PO_CATEGORIES.EMC))
      setFabricationPOs(data.filter(v => v.name === PROJECT_PRODUCTION_PO_CATEGORIES.Fabrication))
      setMonumentPOs(data.filter(v => v.name === PROJECT_PRODUCTION_PO_CATEGORIES.Monument))
      setSteelPOs(data.filter(v => v.name === PROJECT_PRODUCTION_PO_CATEGORIES.Steel))
    } catch (err) {
      // error log here
    }
  }

  const loadProjectShippingPOs = async () => {
    try {
      const data = await getProjectShippingPOsByProjectId(projectId)
      setShippingPOs(data)
    } catch (err) {
      // error log here
    }
  }

  const loadProjectInstallerPOs = async () => {
    try {
      const data = await getProjectInstallerPOsByProjectId(projectId)
      setInstallerPOs(data)
    } catch (err) {
      // error log here
    }
  }

  const loadProjectChangeOrders = async () => {
    try {
      const data = await getProjectChangeOrdersByProjectId(projectId)
      setChangeOrders(data.filter(v => !!v.isApproved))
    } catch (err) {
      // error log here
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadCPM()
      loadQuoteByProjectId()
      loadProjectProductionPOs()
      loadProjectShippingPOs()
      loadProjectInstallerPOs()
      loadProjectChangeOrders()
    }
  }, [projectId, isOpen])

  const quotePriceBreakdown = useMemo(() => {
    return getQuotePriceBreakdown(quote, installationOption)
  }, [quote, installationOption])

  const emcPOsTotalPrice = useMemo(() => {
    return parseFloat(emcPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [emcPOs])

  const fabricationPOsTotalPrice = useMemo(() => {
    return parseFloat(fabricationPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [fabricationPOs])

  const monumentPOsTotalPrice = useMemo(() => {
    return parseFloat(monumentPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [monumentPOs])

  const steelPOsTotalPrice = useMemo(() => {
    return parseFloat(steelPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [steelPOs])

  const shippingPOsTotalPrice = useMemo(() => {
    return parseFloat(shippingPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [shippingPOs])

  const installationPOs = useMemo(() => {
    return installerPOs.filter(po => po.name !== PROJECT_INSTALLER_PO_CATEGORIES.Permit && po.name !== PROJECT_INSTALLER_PO_CATEGORIES.Variance)
  }, [installerPOs])

  const permitPOs = useMemo(() => {
    return installerPOs.filter(po => po.name === PROJECT_INSTALLER_PO_CATEGORIES.Permit)
  }, [installerPOs])

  const variancePOs = useMemo(() => {
    return installerPOs.filter(po => po.name === PROJECT_INSTALLER_PO_CATEGORIES.Variance)
  }, [installerPOs])

  const installerPOsTotalPrice = useMemo(() => {
    return parseFloat(installationPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [installationPOs])

  const permitPOsTotalPrice = useMemo(() => {
    return parseFloat(permitPOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [permitPOs])

  const variancePOsTotalPrice = useMemo(() => {
    return parseFloat(variancePOs.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [variancePOs])

  const changeOrdersTotalPrice = useMemo(() => {
    return parseFloat(changeOrders.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0).toFixed(2))
  }, [changeOrders])

  useEffect(() => {
    const grossSale = (cpm?.grossSale || 0) + changeOrdersTotalPrice
    const cogs = form.emcCost * 1 + emcPOsTotalPrice + form.fabricationCost * 1 + fabricationPOsTotalPrice +
                  form.monumentCost * 1 + monumentPOsTotalPrice + form.steelCost * 1 + steelPOsTotalPrice +
                  form.shippingCost * 1 + shippingPOsTotalPrice + form.installationCost * 1 + installerPOsTotalPrice +
                  form.taxCost * 1 + form.permittingCost * 1 + permitPOsTotalPrice + form.varianceCost * 1 + variancePOsTotalPrice
    const profit = grossSale - cogs - form.overage * 0.5
    const margin = grossSale > 0 ? (profit / grossSale * 100) : 0
    setForm(draft => {
      draft.grossSale = grossSale
      draft.cogs = cogs
      draft.profit = profit
      draft.margin = margin
    })
  }, [
    cpm, form.emcCost, emcPOsTotalPrice, form.fabricationCost, fabricationPOsTotalPrice, form.monumentCost,
    form.steelCost, steelPOsTotalPrice, form.shippingCost, shippingPOsTotalPrice,
    form.installationCost, installerPOsTotalPrice, changeOrdersTotalPrice,
    form.taxCost, form.permittingCost, permitPOsTotalPrice, form.varianceCost, variancePOsTotalPrice, cpm?.grossSale, form.overage
  ])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await upsertCostProfitMatrices(cpm?.id, { ...form, grossSale: (cpm?.grossSale || 0).toFixed(2) })
      setForm(initialForm)
      setIsSaving(false)
      createToast('success', 'Success', 'CPM updated.')
      loadCPM()
    } catch (err) {
      createToast('error', 'Error', 'Failed to update cpm.')
      setIsSaving(true)
    }
  }

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const onClickChangeOrders = () => {
    onClose()
  }

  const onPOsOnly = () => {
    if (!isPOsOnly) {
      setForm(draft => {
        draft.emcCost = 0
        draft.fabricationCost = 0
        draft.monumentCost = 0
        draft.steelCost = 0
        draft.shippingCost = 0
        draft.installationCost = 0
        draft.taxCost = 0
        draft.permittingCost = 0
        draft.varianceCost = 0
      })
    }
    setIsPOsOnly(!isPOsOnly)
  }

  const onUpdateGrossSale = () => {
    disclosureUpdateGrossSale.onOpen()
  }

  const targetMagin = useMemo(() => {
    if (!cpm) {
      return 0
    }

    let margin = (cpm.grossSale || 0) * 0.3
    if (cpm.discount > cpm.overage) {
      margin -= cpm.discount - cpm.overage
    }

    if (cpm.grossSale) {
      return (margin / cpm.grossSale * 100).toFixed(2) * 1
    } else {
      return 0
    }
  }, [cpm])

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cost Profit Matrix</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns="6fr 5fr" columnGap={2} rowGap={2}>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">EMC:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.emcCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.emcCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.emcCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.emcCost * 1 + emcPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {emcPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {emcPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.emcText}
                          onChange={e => {
                            setForm(draft => {
                              draft.emcText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.emcText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.emcText * 1 > 0 ? (((form.emcText * 1 - form.emcCost * 1 - emcPOsTotalPrice) / form.emcText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={2} colSpan={4}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {emcPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.manualName}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">Total: $ {po.price}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <CpmChangeOrderDetail changeOrders={changeOrders} />
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Fabrication:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.fabricationCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.fabricationCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.fabricationCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.fabricationCost * 1 + fabricationPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {fabricationPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {fabricationPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.fabricationText}
                          onChange={e => {
                            setForm(draft => {
                              draft.fabricationText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.fabricationText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.fabricationText * 1 > 0 ? (((form.fabricationText * 1 - form.fabricationCost * 1 - fabricationPOsTotalPrice) / form.fabricationText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {fabricationPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.manualName}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                      <GridItem colStart={11} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.cabinetCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">ID Cabinet</Text></GridItem>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.pedestalCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">Pedestal</Text></GridItem>
                          {(quote?.emcFace === EMC_FACES.SF && !!quote?.emcBackWrapChecked) && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.emcBackWrapCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Back Wrap</Text></GridItem>
                            </>
                          )}
                          {((quote?.emcFace === EMC_FACES.DF || quote?.emcFace === EMC_FACES.DSF) && !!quote?.emcSideVentChecked) && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.emcSideVentCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Side Vent</Text></GridItem>
                            </>
                          )}
                          {!!quote?.customColorChecked && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quotePriceBreakdown?.customColorCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Custom Color</Text></GridItem>
                            </>
                          )}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Gross Sale:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        $
                      </InputLeftAddon>
                      <NumberInput
                        w="100%"
                        value={parseFloat(form.grossSale).toFixed(2)}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </InputGroup>
                  </GridItem>
                  <GridItem colSpan={2}></GridItem>
                  <GridItem colSpan={6}></GridItem>
                  <GridItem colSpan={9}>
                    <Text fontSize="xs" textAlign="right">Original Gross Sale: &nbsp;&nbsp;&nbsp; $ {(cpm?.grossSale || 0).toFixed(2)}</Text>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Monument:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.monumentCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.monumentCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.monumentCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.monumentCost * 1 + monumentPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {monumentPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {monumentPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.monumentText}
                          onChange={e => {
                            setForm(draft => {
                              draft.monumentText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.monumentText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.monumentText * 1 > 0 ? (((form.monumentText * 1 - form.monumentCost * 1 - monumentPOsTotalPrice) / form.monumentText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {monumentPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.manualName}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">COGs:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        $
                      </InputLeftAddon>
                      <NumberInput
                        w="100%"
                        value={parseFloat(form.cogs).toFixed(2)}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Steel:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.steelCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.steelCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.steelCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.steelCost * 1 + steelPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {steelPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {steelPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.steelText}
                          onChange={e => {
                            setForm(draft => {
                              draft.steelText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.steelText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.steelText * 1 > 0 ? (((form.steelText * 1 - form.steelCost * 1 - steelPOsTotalPrice) / form.steelText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {steelPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.manualName}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                      <GridItem colStart={11} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.verticalSupportCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">Vertical Support</Text></GridItem>
                          {!!quote?.plateCost && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.plateCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Plate Cost</Text></GridItem>
                            </>
                          )}
                          {!!quote?.footerStyleCost && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quote?.footerStyleCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Footer Style</Text></GridItem>
                            </>
                          )}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Overage:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        $
                      </InputLeftAddon>
                      <Input
                        type="number"
                        isReadOnly={isLocked}
                        value={form.overage}
                        onChange={e => {
                          setForm(draft => {
                            draft.overage = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.overage = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Shipping:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.shippingCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.shippingCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.shippingCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.shippingCost * 1 + shippingPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {shippingPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {shippingPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.shippingText}
                          onChange={e => {
                            setForm(draft => {
                              draft.shippingText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.shippingText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.shippingText * 1 > 0 ? (((form.shippingText * 1 - form.shippingCost * 1 - shippingPOsTotalPrice) / form.shippingText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {shippingPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.name}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Discount:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        $
                      </InputLeftAddon>
                      <Input
                        type="number"
                        isReadOnly={isLocked}
                        value={form.discount}
                        onChange={e => {
                          setForm(draft => {
                            draft.discount = e.target.value
                          })
                        }}
                        onBlur={e => {
                          setForm(draft => {
                            draft.discount = parseFloat(e.target.value || 0).toFixed(2)
                          })
                        }}
                      />
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Install:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.installationCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.installationCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.installationCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.installationCost * 1 + installerPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {installationPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {installerPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.installationText}
                          onChange={e => {
                            setForm(draft => {
                              draft.installationText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.installationText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.installationText * 1 > 0 ? (((form.installationText * 1 - form.installationCost * 1 - installerPOsTotalPrice) / form.installationText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {installationPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.name}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                      <GridItem colStart={11} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quotePriceBreakdown?.installationCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">Installation</Text></GridItem>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quotePriceBreakdown?.existingSignCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">Existing Sign</Text></GridItem>
                          <GridItem><Text fontSize="xs" textAlign="right">$ {(quotePriceBreakdown?.siteSurveyCost || 0).toFixed(2)}</Text></GridItem>
                          <GridItem><Text fontSize="xs">Site Survey</Text></GridItem>
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Profit:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        $
                      </InputLeftAddon>
                      <NumberInput
                        w="100%"
                        value={parseFloat(form.profit).toFixed(2)}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Tax:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.taxCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.taxCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.taxCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.taxCost).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.taxText}
                          onChange={e => {
                            setForm(draft => {
                              draft.taxText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.taxText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.taxText * 1 > 0 ? (((form.taxText * 1 - form.taxCost * 1) / form.taxText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  isNotExpandable={true}
                />
              </GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Margin:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        %
                      </InputLeftAddon>
                      <NumberInput
                        w="100%"
                        value={parseFloat(form.margin).toFixed(2)}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Permitting:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.permittingCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.permittingCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.permittingCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.permittingCost * 1 + permitPOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {permitPOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {permitPOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.permittingText}
                          onChange={e => {
                            setForm(draft => {
                              draft.permittingText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.permittingText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.permittingText * 1 > 0 ? (((form.permittingText * 1 - form.permittingCost * 1 - permitPOsTotalPrice) / form.permittingText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {permitPOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.name}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                      <GridItem colStart={11} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {!!quotePriceBreakdown?.permitCost && (
                            <>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {(quotePriceBreakdown.permitCost || 0).toFixed(2)}</Text></GridItem>
                              <GridItem><Text fontSize="xs">Professional Permit</Text></GridItem>
                            </>
                          )}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem></GridItem>
              <GridItem>
                <CpmCategoryDetail
                  cost={
                    <Grid templateColumns="repeat(4, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={1}><Text fontSize="sm" textAlign="end">Variance:</Text></GridItem>
                      <GridItem colSpan={2}>
                        <InputGroup>
                          <InputLeftAddon>
                            $
                          </InputLeftAddon>
                          <Input
                            type="number"
                            isReadOnly={isPOsOnly}
                            value={form.varianceCost}
                            onChange={e => {
                              setForm(draft => {
                                draft.varianceCost = e.target.value
                              })
                            }}
                            onBlur={e => {
                              setForm(draft => {
                                draft.varianceCost = parseFloat(e.target.value || 0).toFixed(2)
                              })
                            }}
                          />
                        </InputGroup>
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">$ {parseFloat(form.varianceCost * 1 + variancePOsTotalPrice).toFixed(2)}</Text>
                      </GridItem>
                    </Grid>
                  }
                  poSummary={
                    <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                      <GridItem><Text fontSize="xs">PO Total: {variancePOs.length}</Text></GridItem>
                      <GridItem><Text fontSize="xs" textAlign="right">Total: $ {variancePOsTotalPrice}</Text></GridItem>
                    </Grid>
                  }
                  text={
                    <Grid templateColumns="repeat(3, 1fr)" gap={4} alignItems="center">
                      <GridItem colSpan={2}>
                        <Input
                          type="number"
                          isReadOnly={isLocked}
                          value={form.varianceText}
                          onChange={e => {
                            setForm(draft => {
                              draft.varianceText = e.target.value
                            })
                          }}
                          onBlur={e => {
                            setForm(draft => {
                              draft.varianceText = parseFloat(e.target.value || 0).toFixed(2)
                            })
                          }}
                        />
                      </GridItem>
                      <GridItem colSpan={1}>
                        <Text fontSize="sm">{form.varianceText * 1 > 0 ? (((form.varianceText * 1 - form.varianceCost * 1 - variancePOsTotalPrice) / form.varianceText * 1) * 100).toFixed(2) + '%' : 'NaN'}</Text>
                      </GridItem>
                    </Grid>
                  }
                  detail={
                    <Grid templateColumns="repeat(15, 1fr)" gap={4} mt={2}>
                      <GridItem colStart={4} colSpan={5}>
                        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                          {variancePOs.map(po => (
                            <Fragment key={po.id}>
                              <GridItem><Text fontSize="xs">{po.name}</Text></GridItem>
                              <GridItem><Text fontSize="xs" textAlign="right">$ {po.price.toFixed(2)}</Text></GridItem>
                            </Fragment>
                          ))}
                        </Grid>
                      </GridItem>
                    </Grid>
                  }
                />
              </GridItem>
              <GridItem></GridItem>
              <GridItem></GridItem>
              <GridItem>
                <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center">
                  <GridItem colSpan={6}>
                    <Text fontSize="sm" textAlign="right">Target Margin:</Text>
                  </GridItem>
                  <GridItem colSpan={9}>
                    <InputGroup>
                      <InputLeftAddon>
                        %
                      </InputLeftAddon>
                      <NumberInput
                        w="100%"
                        value={targetMagin}
                        min={0}
                        step={0.01}
                        isReadOnly={true}
                      >
                        <NumberInputField />
                      </NumberInput>
                    </InputGroup>
                  </GridItem>
                </Grid>
              </GridItem>
            </Grid>
            <Flex justifyContent="flex-end" mt={4} gap={4}>
              <Button onClick={() => setIsLocked(!isLocked)}>{isLocked ? 'Unlock' : 'Lock'} Inputs</Button>
              <Button onClick={onClickChangeOrders}>Change Orders</Button>
              <Button onClick={onPOsOnly}>{isPOsOnly ? 'Not POs' : 'POs'} Only</Button>
              <Button onClick={onUpdateGrossSale}>Update Gross Sale</Button>
            </Flex>
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea name="notes" value={form.notes} onChange={handleChange} />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={handleSave} isLoading={isSaving}>
              Save
            </Button>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ModalCPMUpdateGrossSale
        cpm={cpm}
        projectId={projectId}
        reload={loadCPM}
        {...disclosureUpdateGrossSale}
      />
    </>
  )
}

ModalProjectCPM.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  projectId: PropTypes.number
}

const CpmCategoryDetail = ({ cost, poSummary, text, detail, isNotExpandable = false, ...rest }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Box {...rest}>
      <Grid templateColumns="repeat(15, 1fr)" columnGap={4} rowGap={2} alignItems="center">
        <GridItem colSpan={text ? 9 : 13}>
          {cost}
        </GridItem>
        <GridItem colSpan={1} visibility={isNotExpandable ? 'hidden' : 'visible'}>
          <Button p={0} variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? <MdExpandLess fontSize="18px" /> : <MdExpandMore fontSize="18px" />}
          </Button>
        </GridItem>
        {text && (
          <GridItem colSpan={5}>{text}</GridItem>
        )}
      </Grid>
      {poSummary && (
        <Grid templateColumns="repeat(15, 1fr)" columnGap={4} rowGap={2} alignItems="center">
            <GridItem colStart={4} colSpan={5}>{poSummary}</GridItem>
        </Grid>
      )}
      {isExpanded && detail}
    </Box>
  )
}

CpmCategoryDetail.propTypes = {
  cost: PropTypes.node,
  poSummary: PropTypes.node,
  text: PropTypes.node,
  detail: PropTypes.node,
  isNotExpandable: PropTypes.bool
}

const CpmChangeOrderDetail = ({ changeOrders, ...rest }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const changeOrdersTotalPrice = useMemo(() => {
    return changeOrders.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0)
  }, [changeOrders])

  return (
    <Grid templateColumns="repeat(17, 1fr)" columnGap={4} alignItems="center" {...rest}>
      <GridItem colSpan={6}>
        <Text fontSize="sm" textAlign="right">Change Orders:</Text>
      </GridItem>
      <GridItem colSpan={9}>
        <InputGroup>
          <InputLeftAddon>
            $
          </InputLeftAddon>
          <NumberInput
            w="100%"
            value={changeOrdersTotalPrice}
            min={0}
            step={0.01}
            isReadOnly={true}
          >
            <NumberInputField />
          </NumberInput>
        </InputGroup>
      </GridItem>
      <GridItem colSpan={2}>
        <Button p={0} variant="ghost" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <MdExpandLess fontSize="18px" /> : <MdExpandMore fontSize="18px" />}
        </Button>
      </GridItem>
      <GridItem colSpan={6}></GridItem>
      <GridItem colSpan={9}>
        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
          <GridItem><Text fontSize="xs">Total: {changeOrders.length}</Text></GridItem>
          <GridItem><Text fontSize="xs" textAlign="right">Total: $ {changeOrdersTotalPrice}</Text></GridItem>
        </Grid>
      </GridItem>
      <GridItem colSpan={2}></GridItem>
      {isExpanded && (
        <>
          <GridItem colSpan={6}></GridItem>
          <GridItem colSpan={9}>
            <Grid templateColumns="repeat(2, 1fr)" gap={2} mt={2}>
              {changeOrders.map(order => (
                <>
                  <GridItem><Text fontSize="xs">{order.name}</Text></GridItem>
                  <GridItem><Text fontSize="xs" textAlign="right">Total: $ {order.price}</Text></GridItem>
                </>
              ))}
            </Grid>
          </GridItem>
        </>
      )}
    </Grid>
  )
}

CpmChangeOrderDetail.propTypes = {
  changeOrders: PropTypes.arrayOf(PropTypes.object)
}

const ModalCPMUpdateGrossSale = ({ isOpen, onClose, cpm, projectId, reload }) => {
  const { createToast } = useAppContext()
  const [amount, setAmount] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await upsertCostProfitMatrices(cpm?.id, { grossSale: amount * 1, projectId })
      createToast('success', 'Success', 'Gross sale amount updated.')
      onClose()
      setAmount(0)
      setIsSaving(false)
      reload()
    } catch (err) {
      createToast('error', 'Error', 'Failed to update gross sale amount')
      setIsSaving(false)
    }
  }

  return (
    <>
      <Modal size="xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSave}>
            <ModalHeader>Update Gross Sale</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl isRequired>
                <FormLabel>Enter new amount</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    %
                  </InputLeftAddon>
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    onBlur={e => {
                      setAmount(parseFloat(e.target.value || 0).toFixed(2))
                    }}
                  />
                </InputGroup>
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Flex justifyContent="flex-end" gap={4}>
                <Button type="submit" colorScheme="blue" isLoading={isSaving}>Save</Button>
                <Button onClick={onClose}>Close</Button>
              </Flex>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  )
}

ModalCPMUpdateGrossSale.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  cpm: PropTypes.object,
  projectId: PropTypes.number,
  reload: PropTypes.func
}
